import { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Sidebar from './components/Sidebar/Sidebar';
import Header from './components/Header/Header';
import Dashboard from './components/Dashboard/Dashboard';
import Clients from './components/Clients/Clients';
import VehiclesManagement from './components/VehiclesManagement/VehiclesManagement';
import VehicleDetailPage from './components/VehiclesManagement/VehicleDetailPage';
import ClientFormat from './components/ClientFormat/ClientFormat';
import SSTFormat from './components/SSTFormat/SSTFormat';
import Payments from './components/Payments/Payments';
import ProtectedRoute from './components/ProtectedRoute/ProtectedRoute';
// [OUTSIDE DESIGN] — Uncomment when Settings page is needed
import Settings from './components/Settings/Settings';
// [OUTSIDE DESIGN] — Uncomment when Trash page is needed
import Login from './components/Login/Login';
import { ToastContainer } from './components/shared/Toast';
import { ConfirmProvider } from './components/shared/ConfirmContext';
import AddClientModal from './components/Clients/AddClientModal';
import AddVehicleModal from './components/VehiclesManagement/AddVehicleModal';
import UploadSheetWizard from './components/ClientFormat/UploadSheetWizard';
import DriverPayout from './components/DriverPayout/DriverPayout';
import UserManagement from "./components/UserManagement/UserManagement";
import ClientClaim from './components/ClientClaim/ClientClaim';
import DriverPaySlab from './components/DriverPaySlab/DriverPaySlab';
// import ClientClaim from './components/ClientClaim/ClientClaim';
// const AppLayout = ({ children }) => (
//   <div style={{ display: 'flex', flexDirection: 'row', height: '100vh', width: '100vw', maxWidth: '100%', overflowX: 'hidden', overflowY: 'hidden', backgroundColor: '#F4F7F6', color: '#333333', fontFamily: "'Inter', sans-serif" }}>
//     {children}
//   </div>
// );

const MainContent = ({ children }) => (
  <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, overflow: 'hidden' }}>
    {children}
  </div>
);

const ContentArea = ({ children }) => (
  <main style={{ flexGrow: 1, padding: '32px 40px', overflowY: 'auto', overflowX: 'hidden' }}>
    {children}
  </main>
);

const AppLayoutWithSidebar = ({ children, setAddClientOpen, setAddVehicleOpen, setUploadWizardOpen }) => (
  <div style={{ display: 'flex', flexDirection: 'row', height: '100vh', width: '100vw', maxWidth: '100%', overflowX: 'hidden', overflowY: 'hidden', backgroundColor: '#F4F7F6', color: '#333333', fontFamily: "'Inter', sans-serif" }}>
    <Sidebar
      onAddClient={() => setAddClientOpen(true)}
      onAddVehicle={() => setAddVehicleOpen(true)}
      onUploadSheet={() => setUploadWizardOpen(true)}
    />
    <MainContent>
      <Header />
      <ContentArea>
        {children}
      </ContentArea>
    </MainContent>
  </div>
);

const AppRoutes = ({ setAddClientOpen, setAddVehicleOpen, setUploadWizardOpen }) => {
  const location = useLocation();
  const isLoginPage = location.pathname === '/login';
  const token = sessionStorage.getItem('token');

  // Redirect authenticated users away from login page
  if (isLoginPage && token) {
    window.location.href = '/dashboard';
    return null;
  }

  if (isLoginPage) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <AppLayoutWithSidebar
      setAddClientOpen={setAddClientOpen}
      setAddVehicleOpen={setAddVehicleOpen}
      setUploadWizardOpen={setUploadWizardOpen}
    >
      <Routes>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={
          <ProtectedRoute permission="dashboard">
            <Dashboard />
          </ProtectedRoute>
        } />
        <Route path="/clients" element={
          <ProtectedRoute permission="clients">
            <Clients />
          </ProtectedRoute>
        } />
        <Route path="/vehicles" element={
          <ProtectedRoute permission="vehicles">
            <VehiclesManagement />
          </ProtectedRoute>
        } />
        <Route path="/vehicles/:id" element={
          <ProtectedRoute>
            <VehicleDetailPage />
          </ProtectedRoute>
        } />
        <Route path="/client-format" element={
          <ProtectedRoute>
            <ClientFormat />
          </ProtectedRoute>
        } />
        <Route path="/sst-format" element={
          <ProtectedRoute permission="sstFormat">
            <SSTFormat />
          </ProtectedRoute>
        } />
        <Route path="/payments" element={
          <ProtectedRoute permission="payments">
            <Payments />
          </ProtectedRoute>
        } />
        <Route path="/driver-pay-slabs" element={
          <ProtectedRoute permission="driverPaySlab">
            <DriverPaySlab />
          </ProtectedRoute>
        } />
        <Route path="/driver-payout" element={
          <ProtectedRoute permission="driverPayout">
            <DriverPayout />
          </ProtectedRoute>
        } />
        <Route path="/users" element={
          <ProtectedRoute permission="users">
            <UserManagement />
          </ProtectedRoute>
        } />
        <Route path="/client-claim" element={
          <ProtectedRoute permission="clientClaim">
            <ClientClaim />
          </ProtectedRoute>
        } />
        <Route path="/settings" element={
          <ProtectedRoute permission="settings">
            <Settings />
          </ProtectedRoute>
        } />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </AppLayoutWithSidebar>
  );
};

const App = () => {
  const [addClientOpen, setAddClientOpen] = useState(false);
  const [addVehicleOpen, setAddVehicleOpen] = useState(false);
  const [uploadWizardOpen, setUploadWizardOpen] = useState(false);

  return (
    <BrowserRouter>
      <ConfirmProvider>
        <AppRoutes
          setAddClientOpen={setAddClientOpen}
          setAddVehicleOpen={setAddVehicleOpen}
          setUploadWizardOpen={setUploadWizardOpen}
        />

        {/* Global Modals triggered from FAB */}
        <AddClientModal
          isOpen={addClientOpen}
          onClose={() => setAddClientOpen(false)}
          onSave={() => {
            setAddClientOpen(false);
            window.dispatchEvent(new CustomEvent('clientAdded'));
          }}
        />
        <AddVehicleModal
          isOpen={addVehicleOpen}
          onClose={() => setAddVehicleOpen(false)}
          onSave={() => {
            setAddVehicleOpen(false);
            window.dispatchEvent(new CustomEvent('vehicleAdded'));
          }}
        />
        <UploadSheetWizard
          isOpen={uploadWizardOpen}
          onClose={() => setUploadWizardOpen(false)}
          onSave={() => {
            setUploadWizardOpen(false);
            // Delay slightly to ensure backend data is ready
            setTimeout(() => {
              window.dispatchEvent(new CustomEvent('clientSheetUploaded'));
            }, 500);
          }}
        />

        {/* Global Toast Notifications */}
        <ToastContainer />
      </ConfirmProvider>
    </BrowserRouter>
  );
};

export default App;
